import React from "react";
import { Button } from "common-tailwind-components";
import { useTranslation } from "react-i18next";

import type { UpdateStatus } from "../api/types";
import ModalDragStrip from "./ModalDragStrip";

type Props = {
  status: UpdateStatus | null;
  busy: boolean;
  error: string | null;
  requiresRecordingStop?: boolean;
  onApply: () => void;
};

const DEV_BYPASS_STORAGE_KEY = "clank_dev_allow_skip_update_modal";

const hasDeveloperBypass = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.localStorage.getItem(DEV_BYPASS_STORAGE_KEY) === "1";
};

const UpdateInstallModal = ({
  status,
  busy,
  error,
  requiresRecordingStop = false,
  onApply,
}: Props) => {
  const { t } = useTranslation();
  const latest = status?.latest;
  const mustInstall = Boolean(status?.enabled && status?.supported && latest?.installReady);

  if (!mustInstall || hasDeveloperBypass()) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 px-6 py-8 backdrop-blur-sm">
      {/* This modal has no dismiss path, so without a drag handle the window
          would be pinned in place until the update is installed. */}
      <ModalDragStrip />
      <div className="relative z-10 max-h-[calc(100vh-4rem)] w-full max-w-xl overflow-y-auto rounded-3xl border border-outline bg-surface p-8 text-text shadow-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-warning-strong">
          {t("update_required")}
        </p>
        <h2 className="mt-3 text-3xl font-semibold text-text">
          {t("platonic_is_ready_to_install", { version: latest?.version })}
        </h2>
        <p className="mt-4 text-base leading-7 text-text-mid">
          {t(
            "the_update_has_already_been_downloaded_installation_must_be_completed_before_continuing",
          )}
        </p>
        {latest?.releaseNotes && (
          <section className="mt-5 max-h-[40vh] overflow-y-auto rounded-2xl border border-warning/30 bg-warning/10 p-4 text-left">
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-warning-strong">
              {t("release_notes")}
            </p>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-text">
              {latest.releaseNotes}
            </p>
          </section>
        )}
        {error && <p className="mt-4 text-sm text-danger">{error}</p>}
        <div className="mt-8 flex justify-center">
          <Button color="warning" disabled={busy} loading={busy} onClick={onApply}>
            {t(requiresRecordingStop ? "stop_recording_and_install_update" : "install_update_now")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdateInstallModal;
